import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Truck, User, Building, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import logoIcon from '../assets/logo-icon.png';
import logoWordmark from '../assets/logo-wordmark.png';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: searchParams.get('role') || 'buyer',
    company_name: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      const userData = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        company_name: formData.role === 'dealer' ? formData.company_name : null,
        phone: formData.phone || null,
      });
      
      toast.success('¡Registro exitoso!');
      
      // Redirect based on role
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex" data-testid="register-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <img
                src={logoIcon}
                alt=""
                aria-hidden="true"
                className="h-14 w-auto"
              />
              <img
                src={logoWordmark}
                alt="VehiMax - Todos los Vehículos. Una Plataforma"
                className="h-8 w-auto rounded-sm"
              />
            </Link>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Crear cuenta</h1>
            <p className="text-muted-foreground mt-2">
              Únete al marketplace líder de vehículos comerciales
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Type */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold uppercase tracking-wide">Tipo de cuenta</Label>
              <RadioGroup
                value={formData.role}
                onValueChange={handleRoleChange}
                className="grid grid-cols-3 gap-3"
              >
                <div>
                  <RadioGroupItem
                    value="buyer"
                    id="buyer"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="buyer"
                    className="flex flex-col items-center justify-center p-4 border-2 rounded-sm cursor-pointer peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 hover:bg-secondary transition-colors"
                    data-testid="role-buyer"
                  >
                    <User className="h-6 w-6 mb-2" />
                    <span className="text-sm font-semibold">Comprador</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="seller"
                    id="seller"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="seller"
                    className="flex flex-col items-center justify-center p-4 border-2 rounded-sm cursor-pointer peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 hover:bg-secondary transition-colors"
                    data-testid="role-seller"
                  >
                    <Truck className="h-6 w-6 mb-2" />
                    <span className="text-sm font-semibold">Vendedor</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="dealer"
                    id="dealer"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="dealer"
                    className="flex flex-col items-center justify-center p-4 border-2 rounded-sm cursor-pointer peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 hover:bg-secondary transition-colors"
                    data-testid="role-dealer"
                  >
                    <Building className="h-6 w-6 mb-2" />
                    <span className="text-sm font-semibold">Concesionario</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-semibold uppercase tracking-wide">Nombre completo</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                className="h-12"
                placeholder="Juan Pérez"
                data-testid="register-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wide">Correo electrónico</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
                placeholder="john@example.com"
                data-testid="register-email"
              />
            </div>

            {/* Company Name (for dealers) */}
            {formData.role === 'dealer' && (
              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-sm font-semibold uppercase tracking-wide">Nombre de la empresa</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="h-12"
                  placeholder="ABC Truck Sales"
                  data-testid="register-company"
                />
              </div>
            )}

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wide">Teléfono (Opcional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="h-12"
                placeholder="(555) 123-4567"
                data-testid="register-phone"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wide">Contraseña</Label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12"
                placeholder="••••••••"
                testId="register-password"
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-semibold uppercase tracking-wide">Confirma la contraseña</Label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-12"
                placeholder="••••••••"
                testId="register-confirm-password"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="btn-accent w-full"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            {/* Login Link */}
            <p className="text-center text-sm text-muted-foreground">
              ¿Ya tienes una cuenta?{' '}
              <Link to="/login" className="text-accent font-semibold hover:underline" data-testid="login-link">Iniciar sesión</Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/register-showroom.jpg')`,
          }}
        />
        {/* Light gradient — bright at the top so the showroom shows clearly,
            darker at the bottom for headline contrast */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(14,31,56,0.10) 0%, rgba(14,31,56,0.25) 55%, rgba(14,31,56,0.80) 100%)',
          }}
        />
        <div className="absolute inset-x-0 bottom-0 p-12">
          <div className="text-white max-w-md">
            <h2 className="font-heading text-4xl font-bold uppercase tracking-tight mb-4 drop-shadow-lg">
              Únete a miles de vendedores
            </h2>
            <p className="text-white/95 text-lg drop-shadow-md">
              Publica tus vehículos, llega a compradores en todo el país y haz crecer tu negocio con Vehimax.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
